import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  TrendingUp, 
  DollarSign, 
  Clock, 
  Settings, 
  Power, 
  ChevronRight, 
  ChevronDown, 
  ChevronUp, 
  Info, 
  BarChart3, 
  Activity, 
  ArrowRight, 
  AlertTriangle,
  CheckCircle,
  Loader2,
  Repeat,
  ArrowUpRight
} from 'lucide-react';
import InvestmentTiersModal from './InvestmentTiersModal';

interface ArbitrageRobotPageProps {
  currentBtcPrice: number;
  usdtBalance: number;
  robotState: any;
  onUpdateRobotState: (updates: Partial<any>) => void;
  onUpdateBalances: (updates: { usdt_balance?: number, transaction?: any }) => void;
  availableBalance?: number;
}

interface ArbitrageOpportunity {
  id: string;
  buyExchange: string;
  sellExchange: string;
  pair: string;
  buyPrice: number;
  sellPrice: number;
  profitPercentage: number;
  timestamp: Date;
}

const ArbitrageRobotPage: React.FC<ArbitrageRobotPageProps> = ({
  currentBtcPrice,
  usdtBalance,
  robotState,
  onUpdateRobotState,
  onUpdateBalances,
  availableBalance
}) => {
  const { t } = useTranslation();
  const [showInvestmentTiers, setShowInvestmentTiers] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [isAllocating, setIsAllocating] = useState(false);
  const [allocationAmount, setAllocationAmount] = useState('');
  const [showAllocationForm, setShowAllocationForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showTradingLogs, setShowTradingLogs] = useState(false);
  const [isRemovingFunds, setIsRemovingFunds] = useState(false);
  
  // Simulated trading logs
  const [tradingLogs, setTradingLogs] = useState<any[]>([]);
  
  // Simulated arbitrage opportunities
  const [possibleTrades, setPossibleTrades] = useState<ArbitrageOpportunity[]>([]);
  
  // Generate simulated arbitrage opportunities
  useEffect(() => {
    const generateArbitrageOpportunity = () => {
      const exchanges = ['Binance', 'Bybit', 'Kraken', 'Coinbase', 'Kucoin', 'OKX', 'Huobi'];
      const pairs = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'XRP/USDT', 'BNB/USDT', 'ADA/USDT', 'AVAX/USDT'];
      
      const buyExchange = exchanges[Math.floor(Math.random() * exchanges.length)];
      let sellExchange = buyExchange;
      while (sellExchange === buyExchange) {
        sellExchange = exchanges[Math.floor(Math.random() * exchanges.length)];
      }
      
      const pair = pairs[Math.floor(Math.random() * pairs.length)];
      const basePrice = pair === 'BTC/USDT' ? currentBtcPrice : 
                        pair === 'ETH/USDT' ? currentBtcPrice * 0.05 :
                        pair === 'SOL/USDT' ? currentBtcPrice * 0.001 :
                        pair === 'XRP/USDT' ? currentBtcPrice * 0.00001 :
                        pair === 'BNB/USDT' ? currentBtcPrice * 0.01 :
                        pair === 'ADA/USDT' ? currentBtcPrice * 0.00005 :
                        currentBtcPrice * 0.0005;
      
      const buyPrice = basePrice * (1 - Math.random() * 0.005);
      const sellPrice = basePrice * (1 + Math.random() * 0.005);
      const profitPercentage = ((sellPrice - buyPrice) / buyPrice) * 100;
      
      return {
        id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
        buyExchange,
        sellExchange,
        pair,
        buyPrice,
        sellPrice,
        profitPercentage,
        timestamp: new Date()
      };
    };
    
    // Generate initial opportunities
    if (possibleTrades.length === 0) {
      const initialOpportunities = Array.from({ length: 5 }, (_, i) => {
        const opportunity = generateArbitrageOpportunity();
        opportunity.timestamp = new Date(Date.now() - i * 30000); // 30 seconds apart
        return opportunity;
      });
      setPossibleTrades(initialOpportunities);
    }
    
    // Add new opportunities periodically if robot is active
    const interval = setInterval(() => {
      if (robotState?.is_active) {
        setPossibleTrades(prev => {
          const newOpportunity = generateArbitrageOpportunity();
          return [newOpportunity, ...prev].slice(0, 10); // Keep only the 10 most recent
        });
      }
    }, 45000); // Every 45 seconds
    
    return () => clearInterval(interval);
  }, [robotState?.is_active, currentBtcPrice, possibleTrades.length]);
  
  // Generate simulated trading logs
  useEffect(() => {
    if (robotState?.is_active) {
      const generateLog = () => {
        const actions = ['BUY', 'SELL'];
        const exchanges = ['Binance', 'Bybit', 'Kraken', 'Coinbase'];
        const pairs = ['BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'XRP/USDT'];
        
        const action = actions[Math.floor(Math.random() * actions.length)];
        const exchange = exchanges[Math.floor(Math.random() * exchanges.length)];
        const pair = pairs[Math.floor(Math.random() * pairs.length)];
        const amount = (Math.random() * 0.1).toFixed(6);
        const price = (action === 'BUY' ? currentBtcPrice * 0.9999 : currentBtcPrice * 1.0001).toFixed(2);
        const profit = (Math.random() * 0.01).toFixed(6);
        
        return {
          id: Date.now().toString(),
          action,
          pair,
          exchange,
          amount,
          price,
          profit,
          timestamp: new Date().toISOString()
        };
      };
      
      // Generate a new log every 2 minutes if robot is active
      const interval = setInterval(() => {
        const newLog = generateLog();
        setTradingLogs(prev => [newLog, ...prev].slice(0, 100));
      }, 120000);
      
      // Generate initial logs
      if (tradingLogs.length === 0) {
        const initialLogs = Array.from({ length: 10 }, (_, i) => {
          const log = generateLog();
          log.timestamp = new Date(Date.now() - i * 60000).toISOString();
          return log;
        });
        setTradingLogs(initialLogs);
      }
      
      return () => clearInterval(interval);
    }
  }, [robotState?.is_active, currentBtcPrice, tradingLogs.length]);

  // Handle robot activation
  const handleActivateRobot = async () => {
    if (robotState?.allocated_balance <= 0) {
      setError(t('robot.pleaseAllocateBalance'));
      return;
    }
    
    setIsActivating(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update robot state
      await onUpdateRobotState({
        is_active: true,
        todays_profit: 0, // Reset daily profit on activation
        last_profit_timestamp: new Date().toISOString()
      });
      
      setSuccess(t('robot.robotActivatedSuccessfully'));
    } catch (error: any) {
      setError(error.message || t('robot.failedToActivateRobot'));
    } finally {
      setIsActivating(false);
    }
  };

  // Handle robot deactivation
  const handleDeactivateRobot = async () => {
    setIsDeactivating(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update robot state
      await onUpdateRobotState({
        is_active: false
      });
      
      setSuccess(t('robot.robotDeactivatedSuccessfully'));
    } catch (error: any) {
      setError(error.message || t('robot.failedToDeactivateRobot'));
    } finally {
      setIsDeactivating(false);
    }
  };

  // Handle balance allocation
  const handleAllocateBalance = async () => {
    const amount = parseFloat(allocationAmount);
    
    if (isNaN(amount) || amount <= 0) {
      setError(t('errors.invalidAmount'));
      return;
    }
    
    if (availableBalance !== undefined && amount > availableBalance) {
      setError(t('robot.insufficientAvailableBalance', { 
        required: amount.toFixed(2), 
        available: availableBalance.toFixed(2) 
      }));
      return;
    } else if (availableBalance === undefined && amount > usdtBalance) {
      setError(t('errors.insufficientBalance'));
      return;
    }
    
    setIsAllocating(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Update balances
      await onUpdateBalances({
        usdt_balance: usdtBalance - amount,
        transaction: {
          type: 'robot_profit',
          amount: -amount,
          description: t('robot.allocatedBalanceDescription'),
          status: 'completed'
        }
      });
      
      // Update robot state
      await onUpdateRobotState({
        allocated_balance: (robotState?.allocated_balance || 0) + amount
      });
      
      setSuccess(t('robot.successfullyAllocated', { amount: amount.toFixed(2) }));
      setAllocationAmount('');
      setShowAllocationForm(false);
    } catch (error: any) {
      setError(error.message || t('robot.failedToAllocateBalance'));
    } finally {
      setIsAllocating(false);
    }
  };

  // Handle removing allocated balance
  const handleRemoveAllocatedFunds = async () => {
    setIsRemovingFunds(true);
    setError(null);
    setSuccess(null);
    
    try {
      const allocatedAmount = robotState?.allocated_balance || 0;
      
      if (allocatedAmount <= 0) {
        setError(t('robot.noAllocatedFundsToRemove'));
        return;
      }
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Return allocated balance to user's main USDT balance
      await onUpdateBalances({
        usdt_balance: usdtBalance + allocatedAmount,
        transaction: {
          type: 'withdrawal',
          amount: allocatedAmount,
          description: t('robot.fundsRemovedDescription'),
          status: 'completed'
        }
      });
      
      // Reset robot state
      await onUpdateRobotState({
        allocated_balance: 0,
        todays_profit: 0
      });
      
      setSuccess(t('robot.successfullyRemoved', { amount: allocatedAmount.toFixed(2) }));
    } catch (error: any) {
      setError(error.message || t('robot.failedToRemoveFunds'));
    } finally {
      setIsRemovingFunds(false);
    }
  };

  // Calculate daily profit percentage based on allocated balance and BTC price
  const getDailyProfitPercentage = () => {
    const allocatedBalanceInBtc = (robotState?.allocated_balance || 0) / currentBtcPrice;
    
    if (allocatedBalanceInBtc >= 50) return 2.1; // WHALE tier
    if (allocatedBalanceInBtc >= 10) return 1.2; // SHARK tier
    if (allocatedBalanceInBtc >= 5) return 0.8; // DOLPHIN tier
    if (allocatedBalanceInBtc >= 1) return 0.6; // OCTOPUS tier
    if (allocatedBalanceInBtc >= 0.1) return 0.35; // CRAB tier
    return 0.2; // SHRIMP tier
  };

  // Calculate estimated daily profit
  const estimatedDailyProfit = (robotState?.allocated_balance || 0) * (getDailyProfitPercentage() / 100);
  
  // Calculate estimated monthly profit
  const estimatedMonthlyProfit = estimatedDailyProfit * 30;
  
  // Calculate estimated yearly profit
  const estimatedYearlyProfit = estimatedDailyProfit * 365;
  
  // Get investment tier based on allocated balance
  const getInvestmentTier = () => {
    const allocatedBalanceInBtc = (robotState?.allocated_balance || 0) / currentBtcPrice;
    
    if (allocatedBalanceInBtc >= 50) return 'WHALE';
    if (allocatedBalanceInBtc >= 10) return 'SHARK';
    if (allocatedBalanceInBtc >= 5) return 'DOLPHIN';
    if (allocatedBalanceInBtc >= 1) return 'OCTOPUS';
    if (allocatedBalanceInBtc >= 0.1) return 'CRAB';
    if (allocatedBalanceInBtc > 0) return 'SHRIMP';
    return 'NONE';
  };

  // Get tier color
  const getTierColor = () => {
    const tier = getInvestmentTier();
    
    switch (tier) {
      case 'WHALE': return 'text-green-400';
      case 'SHARK': return 'text-red-400';
      case 'DOLPHIN': return 'text-blue-400';
      case 'OCTOPUS': return 'text-purple-400';
      case 'CRAB': return 'text-orange-400';
      case 'SHRIMP': return 'text-gray-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              {t('robot.title')}
            </h1>
            <p className="text-slate-400">{t('robot.subtitle')}</p>
          </div>

          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="bg-slate-800/50 backdrop-blur-sm px-6 py-3 rounded-xl border border-slate-700/50 shadow-lg w-full md:w-auto">
              <div className="text-slate-400 text-sm">{t('header.availableBalance')}</div>
              <div className="text-white font-mono text-xl">${(availableBalance !== undefined ? availableBalance : usdtBalance).toFixed(2)}</div>
            </div>
            
            <div className="flex gap-3 w-full md:w-auto">
              {robotState?.is_active ? (
                <button
                  onClick={handleDeactivateRobot}
                  disabled={isDeactivating}
                  className="flex-1 md:flex-auto bg-red-600 hover:bg-red-700 disabled:bg-slate-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {isDeactivating ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Power size={18} />
                  )}
                  {t('robot.stopRobot')}
                </button>
              ) : (
                <button
                  onClick={handleActivateRobot}
                  disabled={isActivating || robotState?.allocated_balance <= 0}
                  className="flex-1 md:flex-auto bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 disabled:from-slate-700 disabled:to-slate-800 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2"
                >
                  {isActivating ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Power size={18} />
                  )}
                  {t('robot.startRobot')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle size={20} className="text-red-400 flex-shrink-0" />
            <span className="text-red-400">{error}</span>
          </div>
        )}
        
        {success && (
          <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle size={20} className="text-green-400 flex-shrink-0" />
            <span className="text-green-400">{success}</span>
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Robot Status */}
          <div className="lg:col-span-2 space-y-6">
            {/* Robot Status Card */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white">{t('robot.robotStatus')}</h2>
                <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                  robotState?.is_active 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {robotState?.is_active ? t('robot.active') : t('robot.inactive')}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25">
                      <DollarSign size={20} className="text-white" />
                    </div>
                    <div>
                      <div className="text-slate-400 text-sm">{t('robot.allocatedBalance')}</div>
                      <div className="text-white font-bold text-xl">${(robotState?.allocated_balance || 0).toFixed(2)}</div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 mt-4">
                    <button
                      onClick={() => setShowAllocationForm(!showAllocationForm)}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
                    >
                      {showAllocationForm ? t('common.cancel') : t('robot.allocateFunds')}
                    </button>
                    <button
                      onClick={() => setShowInvestmentTiers(true)}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-lg shadow-purple-500/25 flex items-center justify-center gap-2"
                    >
                      {t('robot.investmentTiers')}
                    </button>
                  </div>
                  
                  {/* Remove Allocated Funds Button - Only show when robot is off and has allocated balance */}
                  {!robotState?.is_active && (robotState?.allocated_balance || 0) > 0 && (
                    <div className="mt-3">
                      <button
                        onClick={handleRemoveAllocatedFunds}
                        disabled={isRemovingFunds}
                        className="w-full bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 disabled:from-slate-700 disabled:to-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-lg shadow-red-500/25 flex items-center justify-center gap-2"
                      >
                        {isRemovingFunds ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            {t('robot.processing')}...
                          </>
                        ) : (
                          <>
                            <ArrowUpRight size={16} />
                            {t('robot.removeAllocatedFunds')}
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  
                  {showAllocationForm && (
                    <div className="mt-4 bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                      <div className="mb-3">
                        <label className="block text-sm text-slate-400 mb-1">{t('robot.amountToAllocate')}</label>
                        <div className="flex gap-2">
                          <input
                            type="number"
                            value={allocationAmount}
                            onChange={(e) => setAllocationAmount(e.target.value)}
                            placeholder={t('swap.enterAmount')}
                            className="flex-1 bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-slate-600/50 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                          />
                          <button
                            onClick={() => setAllocationAmount((availableBalance !== undefined ? availableBalance : usdtBalance).toString())}
                            className="bg-slate-700 hover:bg-slate-600 text-slate-300 px-3 py-2 rounded-lg text-xs font-medium"
                          >
                            {t('common.max')}
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={handleAllocateBalance}
                        disabled={isAllocating}
                        className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:from-slate-700 disabled:to-slate-800 text-white py-2 rounded-lg text-sm font-medium transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
                      >
                        {isAllocating ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          t('robot.confirmAllocation')
                        )}
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25">
                      <TrendingUp size={20} className="text-white" />
                    </div>
                    <div>
                      <div className="text-slate-400 text-sm">{t('robot.todaysProfit')}</div>
                      <div className="text-emerald-400 font-bold text-xl">+${(robotState?.todays_profit || 0).toFixed(2)}</div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">{t('robot.investmentTier')}</span>
                      <span className={`font-medium ${getTierColor()}`}>{getInvestmentTier()}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">{t('robot.dailyProfitRate')}</span>
                      <span className="text-emerald-400">{getDailyProfitPercentage()}%</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-400">{t('robot.totalTrades')}</span>
                      <span className="text-white">{robotState?.total_trades || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">{t('robot.successRate')}</span>
                      <span className="text-white">
                        {robotState?.total_trades > 0 
                          ? ((robotState?.successful_trades / robotState?.total_trades) * 100).toFixed(1) 
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Profit Projections */}
              <div className="mt-6 bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                <h3 className="text-lg font-semibold text-white mb-4">{t('robot.profitProjections')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                    <div className="text-slate-400 text-sm mb-1">{t('robot.daily')}</div>
                    <div className="text-emerald-400 font-bold text-lg">+${estimatedDailyProfit.toFixed(2)}</div>
                    <div className="text-xs text-slate-500">{t('robot.ofAllocatedBalance', { percentage: getDailyProfitPercentage() })}</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                    <div className="text-slate-400 text-sm mb-1">{t('robot.monthly')}</div>
                    <div className="text-emerald-400 font-bold text-lg">+${estimatedMonthlyProfit.toFixed(2)}</div>
                    <div className="text-xs text-slate-500">{t('robot.ofAllocatedBalance', { percentage: (getDailyProfitPercentage() * 30).toFixed(1) })}</div>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                    <div className="text-slate-400 text-sm mb-1">{t('robot.yearly')}</div>
                    <div className="text-emerald-400 font-bold text-lg">+${estimatedYearlyProfit.toFixed(2)}</div>
                    <div className="text-xs text-slate-500">{t('robot.ofAllocatedBalance', { percentage: (getDailyProfitPercentage() * 365).toFixed(1) })}</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Possible Arbitrage Opportunities */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Repeat size={20} className="text-blue-400" />
                  {t('robot.possibleOpportunities')}
                </h2>
                <div className="text-xs text-slate-400 bg-slate-700/50 px-3 py-1 rounded-full">
                  {t('robot.opportunitiesCount', { count: possibleTrades.length })}
                </div>
              </div>
              
              {possibleTrades.length > 0 ? (
                <div className="space-y-3 mb-6">
                  {possibleTrades.map((trade) => (
                    <div key={trade.id} className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/40 hover:border-blue-500/30 hover:bg-slate-800/60 transition-all duration-300 shadow-md">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium text-lg">{trade.pair}</span>
                          <span className="text-xs px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 font-bold">
                            +{trade.profitPercentage.toFixed(2)}%
                          </span>
                        </div>
                        <span className="text-xs text-slate-400 bg-slate-700/40 px-2 py-1 rounded-lg">
                          {new Date(trade.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm mt-2">
                        <div className="flex items-center gap-2 bg-slate-900/40 px-3 py-2 rounded-lg">
                          <ArrowRight size={14} className="text-green-400" />
                          <span className="text-slate-300">
                            {t('robot.buy')}: <span className="text-white font-medium">{trade.buyExchange}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2 bg-slate-900/40 px-3 py-2 rounded-lg">
                          <ArrowRight size={14} className="text-red-400" />
                          <span className="text-slate-300">
                            {t('robot.sell')}: <span className="text-white font-medium">{trade.sellExchange}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500 mb-6">
                  <p className="mb-2">{t('robot.noOpportunitiesFound')}</p>
                  <p className="text-sm text-slate-600">{t('robot.activateToScan')}</p>
                </div>
              )}
            </div>
            
            {/* Trading Logs */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <BarChart3 size={20} className="text-blue-400" />
                  {t('robot.tradingLogs')}
                </h2>
                <button 
                  onClick={() => setShowTradingLogs(!showTradingLogs)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  {showTradingLogs ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>
              
              {showTradingLogs ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {tradingLogs.length > 0 ? (
                    tradingLogs.map((log) => (
                      <div key={log.id} className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/40">
                        <div className="flex justify-between items-center mb-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              log.action === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {log.action}
                            </span>
                            <span className="text-white font-medium">{log.pair}</span>
                          </div>
                          <span className="text-xs text-slate-400">
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">{log.exchange} • {log.amount} @ ${log.price}</span>
                          <span className="text-emerald-400">+${log.profit}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <Activity size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="mb-2">{t('robot.noActivity')}</p>
                      <p className="text-sm text-slate-600">{t('robot.activateRobot')}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-400">
                  {t('robot.clickToViewLogs')}
                </div>
              )}
            </div>
          </div>
          
          {/* Right Column - How It Works & Settings */}
          <div className="space-y-8">
            {/* How It Works */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Info size={20} className="text-blue-400" />
                {t('robot.howItWorks')}
              </h2>
              
              <div className="space-y-4">
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/25 text-white font-bold">
                      1
                    </div>
                    <h3 className="text-lg font-medium text-white">{t('robot.marketAnalysis')}</h3>
                  </div>
                  <p className="text-slate-400 text-sm">
                    {t('robot.marketAnalysisDescription')}
                  </p>
                </div>
                
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-lg shadow-purple-500/25 text-white font-bold">
                      2
                    </div>
                    <h3 className="text-lg font-medium text-white">{t('robot.opportunityDetection')}</h3>
                  </div>
                  <p className="text-slate-400 text-sm">
                    {t('robot.opportunityDetectionDescription')}
                  </p>
                </div>
                
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-green-500 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/25 text-white font-bold">
                      3
                    </div>
                    <h3 className="text-lg font-medium text-white">{t('robot.automatedExecution')}</h3>
                  </div>
                  <p className="text-slate-400 text-sm">
                    {t('robot.automatedExecutionDescription')}
                  </p>
                </div>
                
                <div className="bg-slate-900/30 rounded-xl p-4 border border-slate-700/30">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/25 text-white font-bold">
                      4
                    </div>
                    <h3 className="text-lg font-medium text-white">{t('robot.profitAccumulation')}</h3>
                  </div>
                  <p className="text-slate-400 text-sm">
                    {t('robot.profitAccumulationDescription')}
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => setShowInvestmentTiers(true)}
                className="w-full mt-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2"
              >
                {t('robot.viewInvestmentTiers')}
                <ArrowRight size={18} />
              </button>
            </div>
            
            {/* Robot Settings */}
            <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 shadow-2xl">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <Settings size={20} className="text-blue-400" />
                {t('robot.robotSettings')}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">{t('robot.tradingStrategy')}</label>
                  <select
                    value={robotState?.strategy || 'triangular'}
                    onChange={(e) => onUpdateRobotState({ strategy: e.target.value })}
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  >
                    <option value="triangular">{t('robot.strategies.triangular')}</option>
                    <option value="spatial">{t('robot.strategies.spatial')}</option>
                    <option value="statistical">{t('robot.strategies.statistical')}</option>
                    <option value="latency">{t('robot.strategies.latency')}</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    {t('robot.minProfitThreshold')}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.1"
                    max="5"
                    value={robotState?.min_profit_threshold || 0.5}
                    onChange={(e) => onUpdateRobotState({ min_profit_threshold: parseFloat(e.target.value) })}
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  />
                  <p className="text-xs text-slate-500 mt-1 ml-1">
                    {t('robot.minProfitThresholdDescription')}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    {t('robot.maxTradeAmount')}
                  </label>
                  <input
                    type="number"
                    step="10"
                    min="10"
                    max={robotState?.allocated_balance || 1000}
                    value={robotState?.max_trade_amount || 1000}
                    onChange={(e) => onUpdateRobotState({ max_trade_amount: parseFloat(e.target.value) })}
                    className="w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                  />
                  <p className="text-xs text-slate-500 mt-1 ml-1">
                    {t('robot.maxTradeAmountDescription', { min: 10, max: robotState?.allocated_balance || 1000 })}
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => onUpdateRobotState({ 
                  strategy: robotState?.strategy || 'triangular',
                  min_profit_threshold: robotState?.min_profit_threshold || 0.5,
                  max_trade_amount: robotState?.max_trade_amount || 1000
                })}
                className="w-full mt-6 bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white py-3 rounded-xl font-medium transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Settings size={18} />
                {t('robot.saveSettings')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Investment Tiers Modal */}
      {showInvestmentTiers && (
        <InvestmentTiersModal
          isOpen={showInvestmentTiers}
          onClose={() => setShowInvestmentTiers(false)}
          onInvestNow={() => setShowAllocationForm(true)}
        />
      )}
    </div>
  );
};

export default ArbitrageRobotPage;